import React, { useEffect, useState } from 'react';

function toDatetimeLocalValue(isoValue) {
    if (!isoValue) return '';
    const parsed = new Date(isoValue);
    if (Number.isNaN(parsed.getTime())) return '';
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    const hour = String(parsed.getHours()).padStart(2, '0');
    const minute = String(parsed.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hour}:${minute}`;
}

function toIsoFromLocal(value) {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toISOString();
}

function toDatetimeLocalFromDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hour}:${minute}`;
}

function parseLocalDate(value) {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
}

const DEFAULT_REASON_OPTIONS = [
    { value: 'next_year', label: 'Next Year Update' },
    { value: 'next_quarter', label: 'Next Quarter Update' },
    { value: 'six_month', label: '6 Month Update' },
    { value: 'custom', label: 'Custom' },
];

export default function ClientFormScheduleModal({
    isOpen,
    title,
    client,
    schedule,
    submitting,
    onClose,
    onSave,
    onCancelSchedule,
    reasonOptions = DEFAULT_REASON_OPTIONS,
    defaultReason = 'next_year',
}) {
    const [scheduledAt, setScheduledAt] = useState('');
    const [reason, setReason] = useState(defaultReason);
    const [sendEmail, setSendEmail] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        setScheduledAt(toDatetimeLocalValue(schedule?.scheduled_send_at));
        setReason(schedule?.send_reason || defaultReason);
        setSendEmail(schedule?.send_email || client?.email || '');
    }, [isOpen, schedule, client, defaultReason]);

    if (!isOpen || !client) return null;

    const hasActiveSchedule = !!schedule?.scheduled_send_at;
    const saveDisabled = submitting || !scheduledAt || !sendEmail.trim();

    const applyQuickDateOffset = (monthsToAdd) => {
        const current = parseLocalDate(scheduledAt);
        const now = new Date();
        const baseHour = current ? current.getHours() : 8;
        const baseMinute = current ? current.getMinutes() : 0;
        const next = new Date(now.getFullYear(), now.getMonth(), now.getDate(), baseHour, baseMinute, 0, 0);
        next.setMonth(next.getMonth() + monthsToAdd);
        setScheduledAt(toDatetimeLocalFromDate(next));
    };

    const applyQuickTime = (hours, minutes = 0) => {
        const current = parseLocalDate(scheduledAt);
        const base = current || new Date();
        const next = new Date(base);
        next.setHours(hours, minutes, 0, 0);
        setScheduledAt(toDatetimeLocalFromDate(next));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const scheduledIso = toIsoFromLocal(scheduledAt);
        if (!scheduledIso) return;
        await onSave({
            scheduled_send_at: scheduledIso,
            send_reason: reason,
            send_email: sendEmail.trim(),
            reset_progress_on_send: true,
        });
    };

    return (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{title}</h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            <p className="text-muted small mb-3">
                                Schedule the next form email for <strong>{client.name}</strong>.
                                A new magic link will be generated at send time.
                            </p>

                            <div className="mb-3">
                                <label className="form-label">Send Date & Time</label>
                                <input
                                    type="datetime-local"
                                    className="form-control"
                                    value={scheduledAt}
                                    onChange={(e) => setScheduledAt(e.target.value)}
                                    required
                                />
                                <div className="d-flex flex-wrap gap-2 mt-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => applyQuickDateOffset(3)}
                                        disabled={submitting}
                                    >
                                        Today + 3 Months
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => applyQuickDateOffset(6)}
                                        disabled={submitting}
                                    >
                                        Today + 6 Months
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => applyQuickDateOffset(12)}
                                        disabled={submitting}
                                    >
                                        Today + 1 Year
                                    </button>
                                </div>
                                <div className="d-flex flex-wrap gap-2 mt-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => applyQuickTime(8, 0)}
                                        disabled={submitting}
                                    >
                                        8 AM
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => applyQuickTime(12, 0)}
                                        disabled={submitting}
                                    >
                                        12 Noon
                                    </button>
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Reason</label>
                                <select
                                    className="form-select"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                >
                                    {reasonOptions.map((item) => (
                                        <option key={item.value} value={item.value}>{item.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Send To Email</label>
                                <input
                                    type="email"
                                    className="form-control"
                                    value={sendEmail}
                                    onChange={(e) => setSendEmail(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="form-text">
                                Use this for annual or quarterly follow-up requests.
                            </div>
                        </div>
                        <div className="modal-footer">
                            {hasActiveSchedule && (
                                <button
                                    type="button"
                                    className="btn btn-outline-danger me-auto"
                                    onClick={onCancelSchedule}
                                    disabled={submitting}
                                >
                                    Cancel Scheduled Send
                                </button>
                            )}
                            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
                                Close
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={saveDisabled}>
                                {submitting ? 'Saving...' : hasActiveSchedule ? 'Update Schedule' : 'Schedule Send'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
